import { Grid, List } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InventoryType } from "~/utils/constants";
import { useGetLocations } from "~/services/common";
import { useGetClients } from "~/services/client/client.queries";
import { TInventory } from "~/types/inventory";
import {
  InventoryControlByInventory,
  InventoryControlByBooking,
  InventoryBulkUpdate,
} from "~/components/admin/dispatch";
import { useInventoryControlStore } from "./inventory-control.store";
import { Button, DatePicker, Form, Segmented } from "antd";
import { CustomSelect } from "~/components/common";
import {
  FilterOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FileAddOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { PdfImportModal } from "./PdfImportModal";

export const InventoryControl: React.FC = () => {
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const navigate = useNavigate();

  // Zustand store
  const {
    // State
    filters,
    allSelectedInventories,

    // Actions
    setFilters,
    setAllSelectedInventories,
    resetSelections,
  } = useInventoryControlStore();

  const { data: locations, isLoading: locationsLoading } = useGetLocations();
  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { getAll: true },
  });

  // Destructure filters for easier access
  const {
    controlMode,
    selectedDate,
    compactView,
    selectedClient,
    selectedLocation,
    itemType,
    showFilters,
  } = filters;

  // Callback to update selected inventories
  const handleSelectedInventories = useCallback(
    (inventories: TInventory[]) => {
      setAllSelectedInventories(inventories);
    },
    [setAllSelectedInventories]
  );

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Inventory Control</h1>
        <div className="flex items-center gap-4">
          <Button
            icon={<AuditOutlined />}
            onClick={() => navigate("/dispatch/visual-audits")}
          >
            Visual Audit
          </Button>
          <Button
            type="primary"
            icon={<FileAddOutlined />}
            onClick={() => setPdfImportOpen(true)}
          >
            Import from PDF
          </Button>
          <Segmented
            options={[
              { label: "Search by Inventory", value: "inventory" },
              { label: "Search by Bookings", value: "bookings" },
            ]}
            value={controlMode}
            onChange={(value) =>
              setFilters({ controlMode: value as "inventory" | "bookings" })
            }
          />
        </div>
      </div>

      <PdfImportModal
        open={pdfImportOpen}
        onClose={() => setPdfImportOpen(false)}
      />

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex flex-1 space-x-4">
            {controlMode === "bookings" && (
              <DatePicker
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(date) =>
                  setFilters({
                    selectedDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                placeholder="Select date"
                suffixIcon={<CalendarOutlined />}
              />
            )}
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilters({ showFilters: !showFilters })}
            >
              Filters
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={resetSelections}
              type="default"
            >
              Reset
            </Button>
          </div>

          <div className="hidden items-center space-x-2">
            <span className="text-sm text-gray-600">View:</span>
            <Segmented
              options={[
                { label: <List size={16} />, value: false },
                { label: <Grid size={16} />, value: true },
              ]}
              value={compactView}
              onChange={(value) =>
                setFilters({ compactView: value as boolean })
              }
            />
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
            {controlMode === "bookings" && (
              <Form.Item layout="vertical" className="mb-0" label="Vessel">
                <CustomSelect
                  disabled
                  options={[]}
                  placeholder="Select vessel"
                />
              </Form.Item>
            )}
            {controlMode === "inventory" && (
              <>
                <Form.Item layout="vertical" className="mb-0" label="Item Type">
                  <CustomSelect
                    value={itemType}
                    onChange={(value: any) =>
                      setFilters({
                        itemType:
                          value === "all" ? "all" : (value as InventoryType),
                      })
                    }
                    options={[
                      { label: "All Types", value: "all" },
                      { label: "Piece", value: InventoryType.PIECE },
                      { label: "Package", value: InventoryType.PACKAGE },
                      { label: "Part", value: InventoryType.PART },
                    ]}
                  />
                </Form.Item>
                <Form.Item layout="vertical" className="mb-0" label="Location">
                  <CustomSelect
                    value={selectedLocation}
                    onChange={(value: any) =>
                      setFilters({ selectedLocation: value })
                    }
                    options={[
                      { label: "All Locations", value: "all" },
                      ...(locations?.items.map((location: any) => ({
                        label: location.name,
                        value: location.id,
                      })) || []),
                    ]}
                    loading={locationsLoading}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item layout="vertical" className="mb-0" label="Client">
              <CustomSelect
                showSearch
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={selectedClient}
                onChange={(value: any) => setFilters({ selectedClient: value })}
                options={[
                  { label: "All Clients", value: "all" },
                  ...(clients?.items.map((client: any) => ({
                    label:
                      client.companyName ||
                      `${client.firstName} ${client.lastName}`,
                    value: client.id,
                  })) || []),
                ]}
                loading={clientsLoading}
              />
            </Form.Item>
          </div>
        )}
      </div>

      {/* Content */}
      {controlMode === "inventory" && (
        <InventoryControlByInventory
          onSelectedInventories={handleSelectedInventories}
        />
      )}
      {controlMode === "bookings" && (
        <InventoryControlByBooking
          onSelectedInventories={handleSelectedInventories}
        />
      )}

      {/* Bulk Update Options */}
      <InventoryBulkUpdate
        selectedInventories={allSelectedInventories}
        onUpdateComplete={() => {
          resetSelections();
        }}
      />
    </div>
  );
};
